// Reset password landing page — where users land after clicking the link
// in their "reset your password" email.
//
// Firebase Auth flow (Phase 3):
//   1. User triggers reset via /auth/forgot-password → useAuth.resetPassword
//      → sendPasswordResetEmail with actionCodeSettings.url pointing at
//      /auth/sign-in (so a successful reset returns them there).
//   2. Firebase emails a link to its hosted action handler:
//      https://{authDomain}/__/auth/action?mode=resetPassword&oobCode=XXX&...
//   3. By default Firebase's hosted page shows the reset form. We override
//      that by configuring a Custom Action URL in Firebase Console →
//      Authentication → Templates → Password reset → ✏️ → Action URL =
//      `https://socialposts-ai-prod.web.app/auth/reset-password`.
//      Once that's set, Firebase rewrites the link to `{custom_url}?
//      mode=resetPassword&oobCode=XXX&apiKey=...&lang=en` — pointing at
//      THIS page instead of Firebase's hosted handler.
//   4. We verify the oobCode via verifyPasswordResetCode → if it returns
//      an email, the code is valid + we show the form.
//   5. On submit, confirmPasswordReset(auth, oobCode, newPassword). On
//      success → /auth/sign-in (Firebase doesn't sign the user in
//      automatically the way Supabase did).
//
// If the user lands here without an oobCode (bookmark / direct visit),
// we show a "link expired" pane that bounces them to /auth/forgot-password.

import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { verifyPasswordResetCode, confirmPasswordReset } from 'firebase/auth';
import { auth } from '@/lib/firebase';
import { Logo } from '@/components/feature/Logo';

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const oobCode = searchParams.get('oobCode');

  // Tracks whether the URL had a valid oobCode that Firebase accepted.
  // null = still verifying, true = good, false = expired/invalid.
  const [codeValid, setCodeValid] = useState<boolean | null>(null);

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!oobCode) {
      setCodeValid(false);
      return;
    }
    // Validates the code without consuming it — confirmPasswordReset
    // below is the consumer. verifyPasswordResetCode returns the email
    // associated with the code on success; we just need to know it's
    // accepted.
    verifyPasswordResetCode(auth, oobCode)
      .then(() => setCodeValid(true))
      .catch(() => setCodeValid(false));
  }, [oobCode]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    if (!oobCode) {
      setError('Reset link is missing or invalid.');
      return;
    }

    setLoading(true);
    try {
      await confirmPasswordReset(auth, oobCode, password);
      setDone(true);
      // Firebase doesn't auto-sign-in after password reset (unlike
      // Supabase's recovery-session flow). Redirect to sign-in so the
      // user signs in with their new password.
      setTimeout(() => navigate('/auth/sign-in', { replace: true }), 2000);
    } catch (err) {
      const message =
        err && typeof err === 'object' && 'code' in err
          ? String((err as { code: string }).code) === 'auth/expired-action-code'
            ? 'Reset link expired. Request a new one.'
            : String((err as { code: string }).code) === 'auth/weak-password'
              ? 'Password is too weak — use at least 6 characters.'
              : 'Could not reset password. Please request a new link.'
          : 'Could not reset password. Please try again.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  // ---- Loading: still verifying the code ----
  if (codeValid === null) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center p-4">
        <div className="w-full max-w-md text-center">
          <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-6">
            <div className="w-8 h-8 rounded-full border-2 border-primary/20 border-t-primary animate-spin" />
          </div>
          <h2 className="font-display font-bold text-dark text-xl mb-2">Verifying your reset link…</h2>
          <p className="text-dark-muted text-sm">One moment.</p>
        </div>
      </div>
    );
  }

  // ---- Expired / invalid link ----
  if (codeValid === false) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center p-4">
        <div className="w-full max-w-md text-center">
          <div className="w-16 h-16 rounded-2xl bg-amber-50 flex items-center justify-center mx-auto mb-6">
            <i className="ri-time-line text-amber-600 text-2xl" />
          </div>
          <h2 className="font-display font-bold text-dark text-xl mb-3">Reset link expired</h2>
          <p className="text-dark-muted text-sm mb-6">
            This password reset link is no longer valid. Request a fresh one.
          </p>
          <a
            href="/auth/forgot-password"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary hover:bg-primary-hover text-white text-sm font-bold transition-all cursor-pointer"
          >
            <i className="ri-mail-send-line" />
            Send new reset link
          </a>
        </div>
      </div>
    );
  }

  // ---- Success ----
  if (done) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center p-4">
        <div className="w-full max-w-md text-center">
          <div className="w-16 h-16 rounded-2xl bg-emerald-50 flex items-center justify-center mx-auto mb-6">
            <i className="ri-checkbox-circle-line text-emerald-600 text-2xl" />
          </div>
          <h2 className="font-display font-bold text-dark text-xl mb-3">Password updated</h2>
          <p className="text-dark-muted text-sm">Sign in with your new password…</p>
        </div>
      </div>
    );
  }

  // ---- Reset form ----
  return (
    <div className="min-h-screen bg-surface flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <a href="/" className="inline-flex items-center mb-4">
            <Logo height={73} />
          </a>
          <p className="text-dark-muted text-sm">Set a new password</p>
        </div>

        <div className="bg-white rounded-2xl border border-surface-dark/40 p-8 shadow-sm">
          {error && (
            <div className="mb-4 flex items-center gap-2 px-4 py-3 rounded-xl bg-red-50 text-red-700 text-sm font-medium border border-red-200">
              <i className="ri-error-warning-line" />
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="password" className="block text-sm font-semibold text-dark mb-2">
                New password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Min. 6 characters"
                  required
                  minLength={6}
                  className="w-full px-4 py-3 pr-12 rounded-xl border border-surface-dark/60 bg-white text-dark text-sm placeholder:text-dark-muted/50 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center text-dark-muted hover:text-dark transition-colors cursor-pointer"
                >
                  <i className={showPassword ? 'ri-eye-off-line' : 'ri-eye-line'} />
                </button>
              </div>
            </div>

            <div>
              <label htmlFor="confirm" className="block text-sm font-semibold text-dark mb-2">
                Confirm new password
              </label>
              <input
                id="confirm"
                type={showPassword ? 'text' : 'password'}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="Re-enter your password"
                required
                minLength={6}
                className="w-full px-4 py-3 rounded-xl border border-surface-dark/60 bg-white text-dark text-sm placeholder:text-dark-muted/50 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-primary hover:bg-primary-hover text-white text-sm font-bold transition-all cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="w-5 h-5 rounded-full border-2 border-white/30 border-t-white animate-spin" />
              ) : (
                <>
                  <i className="ri-lock-password-line" />
                  Set new password
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
